import { injectable } from 'inversify'
import TreeModel, { Node } from 'tree-model'
import type { Capability } from '@shared'
import type { RouteId } from '@core/routing/router.config'
import type * as HeroIcons from '@heroicons/react/16/solid'

export type NavNode = {
  visible?: boolean
  /**
   * Capability-conditional visibility, unlike the static `visible` flag: the
   * node (and its whole subtree) renders only while the session holds this
   * capability. Display gating only — the matching routes carry
   * `requiresCapability` in router.config.ts and the backend re-checks on every
   * request.
   */
  requiresCapability?: Capability
  /**
   * Gated behind the global admin-mode switch (spec/30-structure.md),
   * on top of whatever `requiresCapability` already requires — same
   * display-only caveat.
   */
  requiresAdminMode?: true
  /**
   * Fixture routes (C11, structural review) — Books, the HR demo, the whole
   * clickable/expandable/columns tree — gated off `Config.showDemoSurface`
   * the same way `requiresAdminMode` gates admin nodes; see `router.config.ts`
   * for the matching `routeDef.requiresDemoSurface`.
   */
  requiresDemoSurface?: true
  clickable?: boolean
  expandable?: boolean
  // Opt-in: instead of expanding inline (indented rows below), this node's children
  // open in a new sidebar column to the right. Only meaningful when `expandable`.
  columnView?: boolean
  /**
   * A heading, not a destination: renders as a label with a divider and a gap
   * either side, and its children are listed *flat* beneath it — at the same
   * depth as the group's own siblings, since a heading adds no nesting.
   *
   * Deliberately not a route (`routesTo` is meaningless here) and never
   * clickable or expandable: it groups links, it is not one. Everything that
   * walks the tree for navigation therefore steps over it — see
   * getRoutableParent, and breadcrumbs, which already skip routeless nodes.
   */
  group?: boolean
  /**
   * Opt-out from menu drill-down: `false` leaves the sidebar anchored on the
   * nearest browsable ancestor when the app navigates here, so this row and
   * its siblings stay listed. For routes whose children are not sidebar
   * material — the page renders them itself, or they are detail routes.
   * Display-only: the children remain in the tree, still reachable by
   * expanding this row (or its column).
   */
  anchorsMenu?: boolean
  /**
   * A translation key, not a label — same split as ThemeConfig.labelKey.
   * NavigationPresenter resolves it, so adding a locale never touches the tree.
   */
  labelKey: string
  routesTo?: RouteId
  urlTo?: string
  sectionTo?: RouteId
  children?: NavNode[]
  preIcon?: keyof typeof HeroIcons
  // Unread-news count badge, right-aligned. The only supported 'post' indicator —
  // there is no free-form postIcon.
  newsCount?: number
}

@injectable()
export class NavigationConfig {
  private _tree: Node<NavNode>

  constructor() {
    this._tree = this.buildTree()
  }

  get tree(): Node<NavNode> {
    return this._tree
  }

  private buildTree(): Node<NavNode> {
    return new TreeModel().parse<NavNode>({
      // routesTo: 'Home' on this wrapper is deliberate, not a stray
      // duplicate of the 'Overview' child below — it is the only routable
      // ancestor a top-level item has, so Up/Section from one of those
      // needs it here to reach Home. It is also never a rendered menu row,
      // though, so anything asking "is this menu row the one I'm on" must
      // not match it — see `navigation-tree.helpers.ts`'s `findRouteNode`
      // and `NavigationRepository.menuRouteNode`.
      labelKey: 'nav.home',
      routesTo: 'Home',
      children: [
        {
          labelKey: 'nav.games',
          clickable: true,
          routesTo: 'Home',
          sectionTo: 'Home',
          preIcon: 'HomeIcon',
          // No children of its own, same reasoning as Play just below — an
          // anchor with no children blanks the whole sidebar.
          anchorsMenu: false,
        },
        // Invisible — neither route is a menu entry, but each must exist in
        // this tree or `NavigationPresenter.viewModel`'s `if (browseNode)`
        // guard blanks the entire sidebar and breadcrumbs on it (see
        // spec/templates/entity-detail-route.md #3).
        {
          labelKey: 'nav.play',
          clickable: true,
          routesTo: 'Lobby',
          sectionTo: 'Home',
          preIcon: 'PuzzlePieceIcon',
          // Play has no children of its own — without this, browsing into
          // Lobby makes Play its own menu anchor (`currentNode`), and an
          // anchor with no children blanks the whole sidebar. Same fix as
          // the Admin.* rows: stay anchored on the parent so Play's row
          // keeps listing among its siblings instead of swallowing them.
          anchorsMenu: false,
        },
        { labelKey: 'nav.gameSetup', visible: false, routesTo: 'Game.Setup', sectionTo: 'Home', anchorsMenu: false },
        { labelKey: 'nav.gamePlay', visible: false, routesTo: 'Game.Play', sectionTo: 'Home', anchorsMenu: false },
        {
          labelKey: 'nav.group.admin',
          group: true,
          children: [
            {
              labelKey: 'nav.adminUsers',
              clickable: true,
              routesTo: 'Admin.Users',
              requiresCapability: 'users.manage',
              requiresAdminMode: true,
              sectionTo: 'Home',
              preIcon: 'UsersIcon',
              anchorsMenu: false,
            },
            {
              labelKey: 'nav.adminAudit',
              clickable: true,
              routesTo: 'Admin.Audit',
              requiresCapability: 'audit.read',
              requiresAdminMode: true,
              sectionTo: 'Home',
              preIcon: 'ClipboardDocumentListIcon',
              anchorsMenu: false,
            },
            {
              labelKey: 'nav.adminErrors',
              clickable: true,
              routesTo: 'Admin.Errors',
              requiresCapability: 'errors.read',
              requiresAdminMode: true,
              sectionTo: 'Home',
              preIcon: 'ExclamationTriangleIcon',
              anchorsMenu: false,
            },
          ],
        },
        // Fixture, like the invisible node it holds: a group with nothing
        // visible in it must render nothing at all — no heading, no gap.
        {
          labelKey: 'nav.group.hidden',
          group: true,
          children: [
            {
              labelKey: 'nav.invisible',
              visible: false,
              routesTo: 'InvisiblePage',
              sectionTo: 'Home',
              preIcon: 'EyeSlashIcon',
            },
          ],
        },
        {
          labelKey: 'nav.group.demo',
          group: true,
          requiresDemoSurface: true,
          children: [
            {
              labelKey: 'nav.books',
              clickable: true,
              routesTo: 'Books',
              sectionTo: 'Home',
              preIcon: 'QuestionMarkCircleIcon',
              newsCount: 3,
            },
            {
              labelKey: 'nav.users',
              clickable: true,
              routesTo: 'HR.Users',
              preIcon: 'UserGroupIcon',
              newsCount: 12,
            },
            {
              labelKey: 'nav.unclickable',
              routesTo: 'UnClickablePage',
              sectionTo: 'Home',
              preIcon: 'LockClosedIcon',
            },
            {
              labelKey: 'nav.clickable',
              clickable: true,
              routesTo: 'ClickablePage',
              sectionTo: 'Home',
              preIcon: 'CursorArrowRaysIcon',
            },
            {
              labelKey: 'nav.expandable',
              expandable: true,
              routesTo: 'ExpandablePage',
              preIcon: 'FolderIcon',
              newsCount: 5,
              children: [
                {
                  labelKey: 'nav.child1',
                  clickable: true,
                  routesTo: 'ExpandablePageChild1',
                  sectionTo: 'Home',
                  preIcon: 'TableCellsIcon',
                },
                {
                  labelKey: 'nav.child2',
                  clickable: true,
                  routesTo: 'ExpandablePageChild2',
                  sectionTo: 'Home',
                  preIcon: 'TableCellsIcon',
                },
              ],
            },
            {
              labelKey: 'nav.both',
              clickable: true,
              expandable: true,
              routesTo: 'BothPage',
              preIcon: 'Square2StackIcon',
              newsCount: 2,
              children: [
                {
                  labelKey: 'nav.child1',
                  clickable: true,
                  routesTo: 'BothPageChild1',
                  sectionTo: 'Home',
                  preIcon: 'TableCellsIcon',
                },
                {
                  labelKey: 'nav.child2',
                  clickable: true,
                  routesTo: 'BothPageChild2',
                  sectionTo: 'Home',
                  preIcon: 'TableCellsIcon',
                },
                {
                  labelKey: 'nav.both',
                  clickable: true,
                  expandable: true,
                  routesTo: 'BothPageBoth',
                  preIcon: 'Square2StackIcon',
                  // Fixture for anchorsMenu: false — this route's children are
                  // not sidebar material, so navigating here keeps the menu on
                  // the parent's children instead of drilling down.
                  anchorsMenu: false,
                  children: [
                    {
                      labelKey: 'nav.child1',
                      clickable: true,
                      routesTo: 'BothPageBothChild1',
                      sectionTo: 'Home',
                      preIcon: 'TableCellsIcon',
                    },
                    {
                      labelKey: 'nav.child2',
                      clickable: true,
                      routesTo: 'BothPageBothChild2',
                      sectionTo: 'Home',
                      preIcon: 'TableCellsIcon',
                    },
                  ],
                },
              ],
            },
            {
              labelKey: 'nav.columns',
              expandable: true,
              columnView: true,
              routesTo: 'ColumnsPage',
              preIcon: 'Squares2X2Icon',
              children: [
                {
                  labelKey: 'nav.settings',
                  clickable: true,
                  routesTo: 'ColumnsPageSettings',
                  sectionTo: 'Home',
                  preIcon: 'Cog6ToothIcon',
                },
                {
                  labelKey: 'nav.advanced',
                  clickable: true,
                  expandable: true,
                  columnView: true,
                  routesTo: 'ColumnsPageAdvanced',
                  preIcon: 'AdjustmentsHorizontalIcon',
                  children: [
                    {
                      labelKey: 'nav.optionA',
                      clickable: true,
                      routesTo: 'ColumnsPageAdvancedOptionA',
                      sectionTo: 'Home',
                      preIcon: 'CheckCircleIcon',
                    },
                    {
                      labelKey: 'nav.optionB',
                      clickable: true,
                      routesTo: 'ColumnsPageAdvancedOptionB',
                      sectionTo: 'Home',
                      preIcon: 'CheckCircleIcon',
                    },
                  ],
                },
              ],
            },
            {
              labelKey: 'nav.infraDemo',
              clickable: true,
              routesTo: 'InfraDemoPage',
              sectionTo: 'Home',
              preIcon: 'BeakerIcon',
            },
            {
              labelKey: 'nav.google',
              clickable: true,
              urlTo: 'https://www.google.com',
              preIcon: 'LinkIcon',
            },
          ],
        },
      ],
    })
  }
}
